import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Building, Sparkles, TrendingUp, ArrowRight, CheckCircle, Star } from 'lucide-react';
import { mockServices, mockTestimonials } from '../mock';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section with Premium Background */}
      <section className="relative h-screen overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1561501900-3701fa6a0864?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbHxlbnwwfHx8fDE3NTk1MTkyNzZ8MA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex items-center h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-6xl md:text-7xl font-light text-white mb-8 leading-tight">
                Luxury Hospitality
                <span className="block font-extralight text-white/90">Redefined</span>
              </h1>
              <p className="text-xl text-white/90 mb-12 leading-relaxed font-light">
                Transform your property into a premium hospitality experience. 
                Trusted by discerning property owners across the USA, UAE, and Africa.
              </p>
              <div className="flex flex-col sm:flex-row gap-6">
                <Link to="/hosts">
                  <Button size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-10 py-6 font-medium">
                    Start Your Journey
                  </Button>
                </Link>
                <Link to="/guests">
                  <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black text-lg px-10 py-6 font-medium">
                    Explore Properties
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Official Partnership Banner */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center space-y-6 md:space-y-0 md:space-x-12">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-light text-white mb-2">Official Hospitality Partner</h3>
              <p className="text-white/90 font-light">OIA Green Ventures & Portfolio Companies</p>
              <p className="text-white/70 font-light text-sm">Since 2023</p>
            </div>
            <div className="flex items-center space-x-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_hostease-global/artifacts/n2vzlpaz_OIA.png" 
                alt="OIA Green Ventures" 
                className="h-16 w-auto object-contain bg-white/10 rounded-lg p-2"
              />
              <div className="text-white/60 text-6xl font-ultralight">×</div>
              <div className="text-white">
                <div className="font-light text-lg">Vantage Points</div>
                <div className="text-sm text-white/80 font-light tracking-wide">HOSPITALITY</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Metrics */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-3">
              <div className="text-4xl font-light text-gray-900">500+</div>
              <p className="text-gray-600 font-light">Premium Properties</p>
            </div>
            <div className="space-y-3">
              <div className="text-4xl font-light text-gray-900">24/7</div>
              <p className="text-gray-600 font-light">Concierge Service</p>
            </div>
            <div className="space-y-3">
              <div className="text-4xl font-light text-gray-900">98%</div>
              <p className="text-gray-600 font-light">Guest Satisfaction</p>
            </div>
            <div className="space-y-3">
              <div className="text-4xl font-light text-gray-900">15+</div>
              <p className="text-gray-600 font-light">Global Markets</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section with Premium Styling */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-light text-gray-900 mb-6">
              Comprehensive Solutions
            </h2>
            <p className="text-xl text-gray-600 font-light max-w-2xl mx-auto leading-relaxed">
              Every detail meticulously managed to deliver exceptional experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {mockServices.map((service, index) => {
              const IconComponent = service.icon === 'Building' ? Building : 
                                  service.icon === 'Sparkles' ? Sparkles : TrendingUp;
              return (
                <div key={service.id} className="group">
                  <Card className="border-0 shadow-sm hover:shadow-xl transition-all duration-500 h-full bg-white">
                    <CardContent className="p-10">
                      <div className="mb-8">
                        <IconComponent className="h-12 w-12 text-gray-800 group-hover:text-black transition-colors duration-300" />
                      </div>
                      <h3 className="text-2xl font-light text-gray-900 mb-6">{service.title}</h3>
                      <p className="text-gray-600 mb-8 leading-relaxed font-light">{service.description}</p>
                      <div className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-gray-700">
                            <CheckCircle className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                            <span className="text-sm font-light">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials with Elegant Design */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-light text-gray-900 mb-6">
              Trusted Worldwide
            </h2>
            <p className="text-xl text-gray-600 font-light">What our partners say about their experience</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {mockTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="text-center">
                <div className="mb-8">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-20 h-20 rounded-full mx-auto object-cover"
                  />
                </div>
                <blockquote className="text-lg text-gray-700 mb-8 leading-relaxed font-light italic">
                  "{testimonial.text}"
                </blockquote>
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`h-4 w-4 mx-1 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
                <h4 className="font-medium text-gray-900 text-lg">{testimonial.name}</h4>
                <p className="text-gray-500 font-light">{testimonial.role} • {testimonial.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbHxlbnwwfHx8fDE3NTk1MTkyNzZ8MA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-light text-white mb-8 leading-tight">
            Ready to Elevate Your Property?
          </h2>
          <p className="text-xl text-white/90 mb-12 font-light leading-relaxed">
            Join the world's most sophisticated property management platform
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/hosts">
              <Button size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-10 py-6 font-medium">
                Become a Partner
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-black text-lg px-10 py-6 font-medium">
                Schedule Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;